import {
  Badge,
  Box,
  Flex,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {
  CartIcon,
  DocumentIcon,
  GlobeIcon,
} from "components/Icons/Icons.js";
import { CalendarIcon } from "@chakra-ui/icons";
import React, { useEffect, useMemo, useState } from "react";
import scheduleService from "services/report/scheduleService.js";
import MiniStatistics from "./components/MiniStatistics";

const sameDay = (tgl, ref = new Date()) => {
  if (!tgl) return false;
  const d = new Date(tgl);
  if (isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === ref.getFullYear() &&
    d.getMonth() === ref.getMonth() &&
    d.getDate() === ref.getDate()
  );
};

const getPersonilNips = (row) =>
  (Array.isArray(row?.personil) ? row.personil : [])
    .map((p) => String(p?.NIP ?? p?.nip ?? "").trim())
    .filter(Boolean);

const unwrapList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  return [];
};

export default function Dashboard() {
  const iconBoxInside = useColorModeValue("white", "white");
  const textColor = useColorModeValue("gray.700", "white");
  const subColor = useColorModeValue("gray.500", "gray.400");
  const myNip = (localStorage.getItem("nip") || "").trim();

  const [loading, setLoading] = useState(true);
  const [jadwal, setJadwal] = useState([]);
  const [pegawai, setPegawai] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const calls = [
          scheduleService.getJadwal(1, 100),
          scheduleService.getInspeksi(1, 100),
          scheduleService.getSKP(1, 100),
          scheduleService.getSurveillance(1, 100),
          scheduleService.getPegawaiInsp(),
        ];
        const settled = await Promise.allSettled(calls);
        if (!alive) return;
        const merged = new Map();
        settled.slice(0, 4).forEach((s) => {
          if (s.status === "fulfilled") {
            unwrapList(s.value).forEach((r) => {
              const key = r?.ID ?? `${r?.NIP}-${r?.TANGGAL}-${r?.NM_UPI}`;
              if (!merged.has(key)) merged.set(key, r);
            });
          }
        });
        setJadwal([...merged.values()]);
        if (settled[4].status === "fulfilled") {
          setPegawai(unwrapList(settled[4].value));
        }
        if (merged.size === 0) setError("Belum ada data jadwal dari API.");
      } catch (e) {
        if (alive) setError("Gagal memuat jadwal hari ini.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const today = useMemo(() => new Date(), []);
  const jadwalHariIni = useMemo(
    () => jadwal.filter((r) => sameDay(r.TANGGAL, today)),
    [jadwal, today]
  );
  const jadwalSaya = useMemo(() => {
    if (!myNip) return [];
    return jadwalHariIni.filter((r) => getPersonilNips(r).includes(myNip));
  }, [jadwalHariIni, myNip]);

  const bertugasNips = useMemo(
    () => new Set(jadwalHariIni.flatMap(getPersonilNips)),
    [jadwalHariIni]
  );
  const petugasKosong = useMemo(() => {
    if (pegawai.length === 0) return [];
    return pegawai.filter((p) => {
      const nip = String(p?.NIP ?? p?.nip ?? "").trim();
      return nip && !bertugasNips.has(nip);
    });
  }, [pegawai, bertugasNips]);

  const statBertugas = jadwalHariIni.length;
  const statSaya = jadwalSaya.length;
  const statKosong = pegawai.length > 0 ? petugasKosong.length : "-";

  const personilNames = (row) => {
    const list = Array.isArray(row?.personil) ? row.personil : [];
    if (list.length === 0) return "-";
    return list.map((p) => p?.NAMA || p?.nama || p?.NIP || "-").join(", ");
  };

  const renderRow = (row) => (
    <Box key={row.ID ?? `${row.NM_UPI}-${row.TANGGAL}`} py={2} borderBottomWidth="1px" borderColor="gray.100">
      <Text fontWeight="bold" color={textColor} fontSize="sm">
        {row.NM_UPI || row.TUJUAN || "-"}
      </Text>
      <Text fontSize="xs" color={subColor}>
        {personilNames(row)} • {row.TANGGAL || "-"}
        {row.JENIS_IZIN ? ` • ${row.JENIS_IZIN}` : ""}
      </Text>
      {row.status?.nm_sts && (
        <Badge mt={1} colorScheme="teal" fontSize="xs">
          {row.status.nm_sts}
        </Badge>
      )}
    </Box>
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <SimpleGrid columns={{ sm: 1, md: 2, xl: 4 }} spacing="24px">
        <MiniStatistics
          title="Jadwal Saya Hari Ini"
          amount={loading ? "..." : String(statSaya)}
          percentage={statSaya}
          icon={<DocumentIcon h="24px" w="24px" color={iconBoxInside} />}
        />
        <MiniStatistics
          title="Tugas Lapangan Hari Ini"
          amount={loading ? "..." : String(statBertugas)}
          percentage={statBertugas}
          icon={<GlobeIcon h="24px" w="24px" color={iconBoxInside} />}
        />
        <MiniStatistics
          title="Petugas Kosong Hari Ini"
          amount={loading ? "..." : String(statKosong)}
          percentage={typeof statKosong === "number" ? statKosong : 0}
          icon={<CartIcon h="24px" w="24px" color={iconBoxInside} />}
        />
        <MiniStatistics
          title="Total Jadwal Terpantau"
          amount={loading ? "..." : String(jadwal.length)}
          percentage={jadwal.length}
          icon={<CalendarIcon h="24px" w="24px" color={iconBoxInside} />}
        />
      </SimpleGrid>

      {loading && (
        <Flex justify="center" py={10}>
          <Spinner />
        </Flex>
      )}
      {error && !loading && (
        <Text color="orange.500" fontSize="sm" mt={4}>
          {error}
        </Text>
      )}

      {!loading && (
        <SimpleGrid columns={{ sm: 1, lg: 3 }} spacing="24px" mt="24px">
          <Card>
            <CardHeader p="12px 0px 8px 0px">
              <Text fontSize="md" color={textColor} fontWeight="bold">
                Jadwal Saya Hari Ini
              </Text>
              <Text fontSize="xs" color={subColor}>
                {myNip ? `NIP: ${myNip}` : "NIP tidak ditemukan di sesi login"}
              </Text>
            </CardHeader>
            <CardBody>
              {jadwalSaya.length === 0 ? (
                <Text fontSize="sm" color={subColor}>
                  Tidak ada jadwal untuk Anda hari ini.
                </Text>
              ) : (
                jadwalSaya.map(renderRow)
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader p="12px 0px 8px 0px">
              <Text fontSize="md" color={textColor} fontWeight="bold">
                Siapa Bertugas Hari Ini
              </Text>
              <Text fontSize="xs" color={subColor}>
                {jadwalHariIni.length} penugasan lapangan
              </Text>
            </CardHeader>
            <CardBody>
              {jadwalHariIni.length === 0 ? (
                <Text fontSize="sm" color={subColor}>
                  Tidak ada petugas bertugas hari ini.
                </Text>
              ) : (
                jadwalHariIni.map(renderRow)
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader p="12px 0px 8px 0px">
              <Text fontSize="md" color={textColor} fontWeight="bold">
                Petugas Kosong Hari Ini
              </Text>
              <Text fontSize="xs" color={subColor}>
                {pegawai.length === 0
                  ? "Daftar pegawai tidak tersedia"
                  : `${petugasKosong.length} dari ${pegawai.length} petugas bebas`}
              </Text>
            </CardHeader>
            <CardBody>
              {pegawai.length === 0 ? (
                <Text fontSize="sm" color={subColor}>
                  Data pegawai inspeksi belum bisa dimuat.
                </Text>
              ) : petugasKosong.length === 0 ? (
                <Text fontSize="sm" color={subColor}>
                  Semua petugas bertugas hari ini.
                </Text>
              ) : (
                petugasKosong.slice(0, 20).map((p, i) => (
                  <Box
                    key={p?.NIP ?? p?.nip ?? i}
                    py={2}
                    borderBottomWidth="1px"
                    borderColor="gray.100"
                  >
                    <Text fontWeight="bold" color={textColor} fontSize="sm">
                      {p?.NAMA ?? p?.nama ?? "-"}
                    </Text>
                    <Text fontSize="xs" color={subColor}>
                      {p?.NIP ?? p?.nip ?? "-"}
                      {p?.JABATAN ? ` • ${p.JABATAN}` : ""}
                    </Text>
                  </Box>
                ))
              )}
            </CardBody>
          </Card>
        </SimpleGrid>
      )}
    </Flex>
  );
}
